import { FC, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import cn from 'classnames';
import NextImage from 'src/shared-components/NextImage/NextImage';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useModalStore from 'src/hooks/useModalStore';
import useSearchInputFocusStore from 'src/hooks/useSearchInputFocusStore';
import useMedia from 'src/hooks/useMedia';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import Skeleton from 'react-loading-skeleton';
import useLocationStore from 'src/hooks/useLocationStore';
import Constraints from '../Constraints/Constraints';
import BurgerButton from './BurgerButton/BurgerButton';
import SearchInputV2, { RefType } from '../SearchInput/SearchInputV2';
import CatalogButton from './CatalogButton/CatalogButton';
import CategoriesDropdown from '../NavigationPanel/CategoriesDropdown';
import MobileCategoriesDropdown from '../NavigationPanel/MobileCategoriesDropdown';
import DropdownElement from '../Dropdown/DropdownElement';
import { FetchAttr, FirstCategoryLevel } from '../../api/types';
import getClient from '../../api/requests/getClient';
import handleAPIError from '../../api/errors';
import useAuthorizationStore from '../../hooks/useAuthorizationStore';
import AuthorizationFormState from '../AuthorizationModal/types';
import Logo from './Logo/Logo';
import UserMenu from './UserMenu/UserMenu';

const AuthorizationModal = dynamic(
  () => import('../AuthorizationModal/AuthorizationModal'),
  {
    ssr: false
  }
);

const CitySelect = dynamic(() => import('../CitySelect/CitySelect'), {
  ssr: false
});

const MobileMenu = dynamic(() => import('./MobileMenu/MobileMenu'), {
  ssr: false
});

export const HEADER_INPUT_ID = 'header-component-id';
const pointIcon = 'shared/point.svg';
const leftArrowIcon = 'shared/left_arrow_icon.svg';
interface Props {
  initialCategory?: FirstCategoryLevel;
  selectedSubCategoryId?: string;
  isBookingCart?: boolean;
  style?: { [key: string]: string };
  modalsStyle?: { [key: string]: string };
}
const Header: FC<Props> = ({
  initialCategory,
  selectedSubCategoryId,
  isBookingCart = false,
  style,
  modalsStyle
}) => {
  const { sm, m, l } = useMedia();
  const { userToken } = useAuthorizationStore();
  const { query, pathname } = useRouter();
  const { updateModalsConfig, isModalVisible } = useModalStore();
  const { trackGtagEvent } = useGTAG();
  const { searchInputFocus, setSearchInputFocus } = useSearchInputFocusStore();
  const { storeInitilized, userLocation } = useLocationStore();
  const isMobileMenuOpen = isModalVisible('mobile-menu-modal');
  const isHomePage = pathname === '/';

  const isMobileCatalogOpen = isModalVisible(
    'mobile-categories-dropdown-modal'
  );

  const isSearchModalOpen = isModalVisible('search-input-v2-modal');

  const headerRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<RefType>(null);

  const [isCatalogOpen, setIsCatalogOpen] = useState<boolean>(false);
  const [userInfo, setUserInfo] =
    useState<{ name: string; surname: string }>(null);
  const [authFormStateForced, setAuthorizationFormState] =
    useState<AuthorizationFormState>(null);

  const isCitySelectedModalOpen = isModalVisible('city-selected-modal');

  const isAuhorizationModalOpen = isModalVisible('authorization-modal');

  const onOpenMobileCatalog = useCallback(() => {
    updateModalsConfig('mobile-menu-modal', false);
    updateModalsConfig('mobile-categories-dropdown-modal', true);
  }, [updateModalsConfig]);

  const closeMenus = useCallback(() => {
    updateModalsConfig('mobile-menu-modal', false);
    updateModalsConfig('mobile-categories-dropdown-modal', false);
    setSearchInputFocus(false);
  }, [updateModalsConfig]);

  const toggleMenus = useCallback(() => {
    if (isMobileCatalogOpen) {
      updateModalsConfig('mobile-categories-dropdown-modal', false);
    }
    if (isMobileMenuOpen) {
      trackGtagEvent(`${GTAGPrefix.menu}_close`);
    } else {
      trackGtagEvent(`${GTAGPrefix.menu}_open`);
    }
    updateModalsConfig('mobile-menu-modal', !isMobileMenuOpen);
  }, [updateModalsConfig, isMobileCatalogOpen, isMobileMenuOpen]);

  const isSearchInputVisible = useMemo(
    () => (isHomePage && searchInputFocus && !sm) || !isHomePage,
    [searchInputFocus]
  );

  const isLeftArrowButtonVisible = useMemo(
    () => !isBookingCart && isSearchInputVisible && isSearchModalOpen,
    [isSearchModalOpen, isSearchInputVisible]
  );

  const cleanInput = () => {
    childRef.current?.handleCleanInput();
    setSearchInputFocus(false);
  };

  useEffect(() => {
    if (pathname === '/catalog') {
      // Close catalog modal with small delay to call after button's `onMouseEnter` trigger on page update
      setTimeout(() => {
        setIsCatalogOpen(false);
      });
    }
  }, [pathname]);

  useEffect(() => {
    if (userToken) {
      (async () => {
        try {
          const attr: FetchAttr = {
            useCache: false,
            authRequired: true
          };
          const response = await getClient(attr);
          setUserInfo({
            name: response?.name,
            surname: response?.surname
          });
        } catch (error) {
          handleAPIError(error);
        }
      })();
    }
  }, [userToken]);

  useEffect(() => {
    if (isCatalogOpen && !m) {
      setIsCatalogOpen(false);
      onOpenMobileCatalog();
    }
    if (isMobileMenuOpen && l) {
      updateModalsConfig('mobile-menu-modal', false);
    }

    if (isMobileCatalogOpen && m) {
      updateModalsConfig('mobile-categories-dropdown-modal', false);
      setIsCatalogOpen(true);
    }
  }, [l, m, updateModalsConfig]);

  useEffect(() => {
    if (query?.registrationOpen === 'true') {
      updateModalsConfig('authorization-modal', true);
      setAuthorizationFormState(AuthorizationFormState.REGISTER);
      closeMenus();
    }
  }, [query?.registrationOpen, updateModalsConfig]);

  const onCloseCatalogs = useCallback(() => {
    updateModalsConfig('mobile-categories-dropdown-modal', false);
    setIsCatalogOpen(false);
  }, [updateModalsConfig]);

  useEffect(() => {
    if (isCitySelectedModalOpen || isAuhorizationModalOpen || isCatalogOpen) {
      setSearchInputFocus(false);
    }
  }, [isCitySelectedModalOpen, isAuhorizationModalOpen, isCatalogOpen]);

  return (
    <header
      onMouseLeave={() => setIsCatalogOpen(false)}
      className={cn(
        'fixed top-0 flex bg-astronautBlue w-full sm:h-9.5 z-70 border-white',
        isBookingCart && 'm:w-1/2',
        isSearchInputVisible ? 'h-[112px]' : 'h-[62px]'
      )}
      ref={headerRef}
      style={style}
    >
      <Constraints
        className={cn(
          'px-2 flex flex-col justify-center',
          !isBookingCart && 'xl:px-0'
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-start sm:w-3/4">
            <div className="flex items-center">
              <Logo
                classNameContainer={cn(
                  'hidden l:flex',
                  isBookingCart && '!flex'
                )}
                gtagPrefix={GTAGPrefix.header}
              />
              {isLeftArrowButtonVisible ? (
                <button className="l:hidden" onClick={cleanInput}>
                  <NextImage
                    alt="left arrow"
                    src={leftArrowIcon}
                    height={24}
                    width={24}
                  />
                </button>
              ) : (
                <>
                  {!isBookingCart && (
                    <BurgerButton
                      isOpen={isMobileMenuOpen || isMobileCatalogOpen}
                      onClick={toggleMenus}
                    />
                  )}
                </>
              )}
              {!isBookingCart && (
                <CatalogButton
                  handleOpenCatalog={() => setIsCatalogOpen((state) => !state)}
                  gtagPrefix={GTAGPrefix.header}
                />
              )}

              <Logo
                classNameContainer="flex sm:hidden ml-2"
                gtagPrefix={GTAGPrefix.header}
                isSmall={true}
              />
            </div>
            <div className="hidden sm:flex w-full h-full ">
              {isSearchInputVisible && (
                <SearchInputV2
                  parentRef={headerRef}
                  isBookingCart={isBookingCart}
                  gtagPrefix={GTAGPrefix.speedsearch}
                  onHandleInputActions={onCloseCatalogs}
                  isAnotherModalOpened={isCatalogOpen}
                  ref={childRef}
                  style={modalsStyle}
                  dataCy="search-input-v2-desktop"
                  containerClassName="hidden sm:flex"
                  modalContainerClassName="hidden sm:flex"
                />
              )}
            </div>
          </div>
          <div
            className={cn(
              'flex items-center sm:w-1/4',
              isBookingCart ? 'justify-end' : 'justify-between'
            )}
          >
            {!isBookingCart && (
              <div className="hidden lm:flex flex-row align-center">
                {!storeInitilized || !userLocation?.name ? (
                  <div className="flex mr-9">
                    <div className="flex justify-center items-center mr-1.5">
                      <NextImage
                        alt="point"
                        src={pointIcon}
                        layout="fixed"
                        quality={100}
                        height={26}
                        width={20}
                        priority
                      />
                    </div>
                    <Skeleton width={46} height={26} />
                  </div>
                ) : (
                  <CitySelect
                    selectorClassName="fs-body-lg-bold mr-9 text-white"
                    additionalSelectorChild={
                      <div className="flex justify-center items-center mr-1.5">
                        <NextImage
                          alt="point"
                          src={pointIcon}
                          layout="fixed"
                          quality={100}
                          height={26}
                          width={20}
                          priority
                        />
                      </div>
                    }
                    gtagPrefix={GTAGPrefix.header}
                  />
                )}
              </div>
            )}
            <UserMenu
              darkStyle
              isHideMenu={true}
              clientInfo={userInfo}
              gtagPrefix={GTAGPrefix.header}
            />
          </div>
        </div>
        {isSearchInputVisible && (
          <div className="flex sm:hidden items-center justify-between sm:mt-0 mb-3">
            <SearchInputV2
              parentRef={headerRef}
              isBookingCart={isBookingCart}
              inputId={HEADER_INPUT_ID}
              gtagPrefix={GTAGPrefix.speedsearch}
              onHandleInputActions={onCloseCatalogs}
              isAnotherModalOpened={isCatalogOpen}
              ref={childRef}
              style={modalsStyle}
              isOnHeader={true}
              dataCy="search-input-v2-mobile"
              containerClassName="flex sm:hidden"
              modalContainerClassName="flex sm:hidden"
              globalContainerClassName="flex sm:hidden"
            />
          </div>
        )}
      </Constraints>

      <AuthorizationModal
        style={modalsStyle}
        formStateForced={authFormStateForced}
      />
      <DropdownElement
        parentRef={headerRef}
        isOpen={isCatalogOpen}
        placement="bottom"
        customStyles={modalsStyle}
        className={cn(
          // Paddings are required to avoid drop-shadow from being cut
          // Shadow should be defined inside CategoriesDropdown because of the Safari bug
          'bg-transparent w-full m:pb-3 m:pr-3 m:pl-3',
          'compendium-constraints compendium-max-h-full-minus-20 overflow-y-auto',
          'hidden' && !m
        )}
      >
        <CategoriesDropdown
          onMouseOver={() => setIsCatalogOpen(true)}
          onCloseCatalog={() => setIsCatalogOpen(false)}
          initialCategory={initialCategory}
          selectedSubCategoryId={selectedSubCategoryId}
        />
      </DropdownElement>
      <MobileMenu
        style={modalsStyle}
        parentRef={headerRef}
        isBookingCart={isBookingCart}
        onCloseMenu={() => updateModalsConfig('mobile-menu-modal', false)}
        onOpenCatalogMenu={onOpenMobileCatalog}
      />
      <MobileCategoriesDropdown
        style={modalsStyle}
        parentRef={headerRef}
        onCloseMenu={toggleMenus}
        onGoToCategory={closeMenus}
        initialCategory={initialCategory}
        selectedSubCategoryId={selectedSubCategoryId}
      />
    </header>
  );
};

export default Header;
